import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';

import { filter, skip, Subscription, take } from 'rxjs';

import { AdvancedSearchService } from '../../../services/advanced/advanced-search.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import {
  isBackgroundRefreshPending,
  isSearchResultReady,
  isVendorSyncComplete,
} from '../../../services/search-polling/osint-job-poll-response.util';
import { UtilsService } from '../../../services/utils/utils.service';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

@Component({
  selector: 'app-advanced-search-result',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    HeaderComponent,
    SidebarComponent,
    PaginatorComponent,
  ],
  templateUrl: './advanced-search-result.component.html',
  styleUrl: './advanced-search-result.component.css',
})
export class AdvancedSearchResultComponent implements OnInit, OnDestroy {
  jobId: string = '';
  searchId: string = '';
  selectedSearchResult: any;
  /** Unfiltered list for the current page (from API). */
  nameResultsCurrentPage: any[] = [];
  /** Displayed list (filtered by search box when searchTerm is set). */
  nameResults: any[] = [];
  searchedName: string = '';

  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;
  backgroundRefreshPending: boolean = false;
  /** True when vendor sync finished with zero people rows. */
  hasNoResults: boolean = false;
  /** Vendor / network failure message (from notes or job status). */
  searchErrorMessage: string = '';

  jobResultSubscription?: Subscription;
  vendorSyncSubscription?: Subscription;
  private pollingActive = false;

  // Pagination properties
  pagination: any = {
    total_items: 0,
    current_page: 1,
    page_size: 100,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  };

  get isLoadingResults(): boolean {
    const hasResults = this.nameResults.length > 0;
    return (
      (this.loadingCompletedSearchResult || this.loadingPolledSearchResult) &&
      !hasResults
    );
  }

  /** Small non-blocking chip while hydrate is shown and vendor still runs. */
  /** Empty or failed terminal state (hide spinner, show message). */
  get showEmptyState(): boolean {
    return (
      !this.isLoadingResults &&
      this.hasNoResults &&
      this.nameResultsCurrentPage.length === 0
    );
  }

  searchTerm: string = '';
  notes: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private searchPollingService: SearchPollingService,
    private advancedSearchService: AdvancedSearchService,
    public utilsService: UtilsService,
  ) {}

  ngOnInit() {
    this.selectedSearchResult = history.state?.searchResult;
    this.jobId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.selectedSearchResult && !this.jobId) {
      this.router.navigate(['/advanced-search']);
      return;
    }

    if (this.selectedSearchResult) {
      this.searchId =
        this.selectedSearchResult.name_search_id || this.searchId;
      this.jobId =
        this.jobId ||
        this.selectedSearchResult.job_id ||
        '';
      this.searchedName =
        this.selectedSearchResult.searched_name ||
        this.selectedSearchResult.name ||
        '';
      this.backgroundRefreshPending = Boolean(
        this.selectedSearchResult.background_refresh_pending,
      );

      if (this.selectedSearchResult.done) {
        this.getCompletedSearchResult(1);
        if (this.backgroundRefreshPending) {
          this.watchVendorSyncThenReload();
        }
      } else {
        this.pollForSearchResult();
      }
    } else if (this.jobId) {
      this.pollForSearchResult();
    }
  }

  ngOnDestroy() {
    this.jobResultSubscription?.unsubscribe();
    this.vendorSyncSubscription?.unsubscribe();
    this.pollingActive = false;
  }

  onBackButtonClick() {
    window.history.back();
  }

  /** Stop polling and show empty / error UI when the search is terminal with no rows. */
  private markTerminalEmpty(options?: {
    errorMessage?: string;
    notes?: string;
  }) {
    this.hasNoResults = true;
    this.loadingCompletedSearchResult = false;
    this.loadingPolledSearchResult = false;
    this.backgroundRefreshPending = false;
    this.pollingActive = false;
    this.jobResultSubscription?.unsubscribe();
    this.vendorSyncSubscription?.unsubscribe();

    const raw = (
      options?.errorMessage ||
      options?.notes ||
      this.searchErrorMessage ||
      ''
    ).trim();
    this.searchErrorMessage = this.friendlySearchError(raw);

    if (this.selectedSearchResult) {
      this.selectedSearchResult = {
        ...this.selectedSearchResult,
        done: true,
        background_refresh_pending: false,
        status: this.searchErrorMessage ? 'failed' : 'completed',
        ...(this.searchErrorMessage
          ? { notes: this.searchErrorMessage }
          : {}),
      };
    }

    if (this.jobId) {
      this.searchPollingService.stopTrackingJobIds([this.jobId]);
    }
  }

  private friendlySearchError(message: string): string {
    if (!message) {
      return '';
    }
    const lower = message.toLowerCase();
    if (
      lower.includes('etimedout') ||
      lower.includes('econnaborted') ||
      lower.includes('econnrefused') ||
      lower.includes('enotfound') ||
      lower.includes('network') ||
      lower.includes('timeout')
    ) {
      return 'Unable to reach the Advanced Search data source. Please try again later or check network access to the name search service.';
    }
    if (lower.includes('name search api failed')) {
      return 'Advanced Search could not complete. Please try again later.';
    }
    return message;
  }

  getCompletedSearchResult(page: number = 1) {
    if (!this.searchId && !this.jobId) {
      return;
    }

    const hasVisibleResults = this.nameResultsCurrentPage.length > 0;
    // Avoid wiping painted hydrate results while a background refetch runs.
    if (!hasVisibleResults) {
      this.loadingCompletedSearchResult = true;
    }

    const originalSearchedName =
      this.selectedSearchResult?.searched_name ||
      this.selectedSearchResult?.name ||
      this.searchedName;

    this.advancedSearchService
      .getCompletedSearchResult(this.searchId, page, {
        jobId: this.jobId || undefined,
      })
      .subscribe({
        next: (response) => {
          this.searchTerm = '';

          const rawSearchResultData = response?.data;
          const messageKey = String(response?.message_key || '');
          const isNoResultsMessage =
            messageKey.toLowerCase() === 'no search results found';

          const refreshPending = Boolean(
            rawSearchResultData?.background_refresh_pending,
          );
          this.backgroundRefreshPending = refreshPending;

          if (rawSearchResultData?.name_search_id) {
            this.searchId = rawSearchResultData.name_search_id;
          }
          if (rawSearchResultData?.job_id) {
            this.jobId = rawSearchResultData.job_id;
          }

          const dataList = rawSearchResultData?.data_list;
          if (!Array.isArray(dataList) || dataList.length === 0) {
            const done =
              Boolean(rawSearchResultData?.done) || isNoResultsMessage;
            const status =
              rawSearchResultData?.status ||
              (refreshPending
                ? 'refreshing'
                : done
                  ? rawSearchResultData?.notes
                    ? 'failed'
                    : 'completed'
                  : 'pending');

            this.selectedSearchResult = {
              ...(this.selectedSearchResult || {}),
              name_search_id: this.searchId,
              job_id: this.jobId,
              done,
              background_refresh_pending: refreshPending,
              status,
              searched_name: originalSearchedName,
              ...(rawSearchResultData?.notes
                ? { notes: rawSearchResultData.notes }
                : {}),
            };
            this.loadingCompletedSearchResult = false;
            this.loadingPolledSearchResult = false;

            const terminal =
              isNoResultsMessage ||
              isVendorSyncComplete(this.selectedSearchResult);

            if (terminal) {
              this.markTerminalEmpty({
                notes: rawSearchResultData?.notes,
                errorMessage:
                  status === 'failed' || status === 'error'
                    ? rawSearchResultData?.notes ||
                      messageKey ||
                      'Advanced search failed'
                    : undefined,
              });
              return;
            }

            if (!this.pollingActive) {
              this.pollForSearchResult();
            }
            return;
          }

          const completedSearchResult = dataList[0];

          this.selectedSearchResult = {
            ...completedSearchResult,
            searched_name: originalSearchedName,
            name_search_id:
              completedSearchResult?.name_search_id || this.searchId,
            job_id: completedSearchResult?.job_id || this.jobId,
            done: true,
            background_refresh_pending: refreshPending,
            status: refreshPending ? 'refreshing' : 'completed',
          };

          this.searchId =
            this.selectedSearchResult.name_search_id || this.searchId;
          this.jobId = this.selectedSearchResult.job_id || this.jobId;
          this.notes = rawSearchResultData?.notes || '';
          this.searchedName = originalSearchedName;
          this.hasNoResults = false;
          this.searchErrorMessage = '';

          // Extract Name results array from the response
          if (completedSearchResult?.name_data?.data) {
            this.nameResultsCurrentPage =
              completedSearchResult.name_data.data.map((item: any) => {
                // Extract only _source fields (excluding metadata like _id, _score)
                const sourceData: any = {};
                if (item._source) {
                  Object.keys(item._source).forEach((key) => {
                    sourceData[key] = item._source[key];
                  });
                }
                return sourceData;
              });
            this.applySearchFilter();
          } else {
            this.nameResultsCurrentPage = [];
            this.nameResults = [];
          }

          // Extract pagination data
          if (completedSearchResult?.name_data?.meta?.pagination) {
            this.pagination = {
              total_items:
                completedSearchResult.name_data.meta.pagination.total_items ||
                0,
              current_page:
                completedSearchResult.name_data.meta.pagination.current_page ||
                1,
              page_size:
                completedSearchResult.name_data.meta.pagination.page_size ||
                100,
              total_pages:
                completedSearchResult.name_data.meta.pagination.total_pages ||
                0,
              has_next:
                completedSearchResult.name_data.meta.pagination.has_next ||
                false,
              has_previous:
                completedSearchResult.name_data.meta.pagination.has_previous ||
                false,
            };
          }

          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;

          if (
            !refreshPending &&
            this.nameResultsCurrentPage.length === 0
          ) {
            this.markTerminalEmpty({
              notes: rawSearchResultData?.notes,
            });
            return;
          }

          if (refreshPending) {
            this.watchVendorSyncThenReload();
          } else if (this.jobId) {
            this.searchPollingService.stopTrackingJobIds([this.jobId]);
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;

          const errData = error?.error?.data;
          if (errData && isVendorSyncComplete(errData)) {
            this.markTerminalEmpty({
              notes: errData?.notes,
              errorMessage:
                errData?.message ||
                errData?.notes ||
                error?.error?.message_key ||
                'Advanced search failed',
            });
            return;
          }

          if (!this.pollingActive && this.jobId) {
            this.pollForSearchResult();
          }
        },
      });
  }

  onPageChange(page: number) {
    this.getCompletedSearchResult(page);
  }

  pollForSearchResult() {
    if (!this.jobId) {
      this.jobId = this.selectedSearchResult?.job_id;
    }

    if (!this.jobId) {
      console.error('No job ID available for polling');
      return;
    }

    if (this.pollingActive) {
      this.ensureAdvancedBackgroundPolling();
      return;
    }

    this.pollingActive = true;
    this.ensureAdvancedBackgroundPolling();

    this.jobResultSubscription?.unsubscribe();
    if (!this.nameResultsCurrentPage.length) {
      this.loadingPolledSearchResult = true;
    }

    const originalSearchedName =
      this.selectedSearchResult?.searched_name ||
      this.selectedSearchResult?.name ||
      this.searchedName;

    this.jobResultSubscription = this.searchPollingService
      .getJobResult(this.jobId)
      .pipe(skip(1))
      .subscribe({
        next: (response) => {
          if (!response) {
            return;
          }

          if (response.name_search_id) {
            this.searchId = response.name_search_id;
          }

          const refreshPending = isBackgroundRefreshPending(response);
          this.backgroundRefreshPending = refreshPending;

          if (response?.notes) {
            this.searchErrorMessage = String(response.notes);
          }

          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            job_id: this.jobId,
            name_search_id: this.searchId || response.name_search_id,
            done: response.done || false,
            status: response.status || 'pending',
            background_refresh_pending: refreshPending,
            searched_name:
              response?.searched_name || originalSearchedName,
            ...(response?.notes ? { notes: response.notes } : {}),
          };

          // Early hydrate: show results as soon as done/ready, keep polling.
          if (
            isSearchResultReady(response) &&
            !this.nameResultsCurrentPage.length &&
            !isVendorSyncComplete(response)
          ) {
            this.getCompletedSearchResult(1);
          }

          if (isVendorSyncComplete(response)) {
            this.backgroundRefreshPending = false;
            this.pollingActive = false;
            this.jobResultSubscription?.unsubscribe();

            const failed =
              response.status === 'failed' ||
              response.status === 'error' ||
              response.timed_out === true ||
              (Boolean(response.notes) &&
                !this.nameResultsCurrentPage.length);

            if (failed && !this.nameResultsCurrentPage.length) {
              this.markTerminalEmpty({
                notes: response.notes || this.searchErrorMessage,
                errorMessage:
                  response.notes ||
                  response.message ||
                  this.searchErrorMessage ||
                  'Advanced search failed',
              });
              return;
            }

            this.getCompletedSearchResult(1);
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingPolledSearchResult = false;
          this.pollingActive = false;
        },
      });
  }

  private watchVendorSyncThenReload() {
    if (!this.jobId) {
      this.jobId = this.selectedSearchResult?.job_id;
    }
    if (!this.jobId) {
      return;
    }

    this.ensureAdvancedBackgroundPolling();
    this.vendorSyncSubscription?.unsubscribe();
    this.vendorSyncSubscription = this.searchPollingService
      .getJobResult(this.jobId)
      .pipe(
        filter((s) => !!s && isVendorSyncComplete(s)),
        take(1),
      )
      .subscribe((status) => {
        this.backgroundRefreshPending = false;
        if (this.selectedSearchResult) {
          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            background_refresh_pending: false,
            status: status?.status === 'failed' ? 'failed' : 'completed',
            done: true,
            ...(status?.notes ? { notes: status.notes } : {}),
          };
        }

        if (
          (status?.status === 'failed' ||
            status?.status === 'error' ||
            status?.timed_out) &&
          !this.nameResultsCurrentPage.length
        ) {
          this.markTerminalEmpty({
            notes: status?.notes,
            errorMessage:
              status?.notes ||
              status?.message ||
              'Advanced search failed',
          });
          return;
        }

        // Final refetch — union merge may have added people.
        this.getCompletedSearchResult(1);
      });
  }

  private ensureAdvancedBackgroundPolling() {
    if (!this.jobId) {
      return;
    }
    this.searchPollingService.ensureBackgroundPolling(
      [
        {
          job_id: this.jobId,
          searchType: 'advanced',
          label:
            this.searchedName ||
            this.selectedSearchResult?.searched_name ||
            this.selectedSearchResult?.name ||
            this.jobId,
        },
      ],
      3 * 1000,
    );
  }

  onSearchTermChange(): void {
    this.applySearchFilter();
  }

  private applySearchFilter(): void {
    const term = (this.searchTerm || '').trim().toLowerCase();
    if (!term) {
      this.nameResults = [...this.nameResultsCurrentPage];
      return;
    }
    this.nameResults = this.nameResultsCurrentPage.filter((item) =>
      this.itemMatchesSearchTerm(item, term),
    );
  }

  private itemMatchesSearchTerm(
    item: Record<string, unknown>,
    term: string,
  ): boolean {
    return Object.values(item).some((val) => {
      if (val == null) return false;
      return String(val).toLowerCase().includes(term);
    });
  }

  onExportOptionClick(exportFormat: string) {
    this.exportSearchResult(exportFormat);
  }

  exportSearchResult(format: string) {
    this.advancedSearchService
      .exportSearchResult(this.searchId, format)
      .subscribe({
        next: (response) => {
          this.saveFile(response?.data);
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  saveFile(fileInfo: any) {
    const { fileName, mimeType, base64 } = fileInfo;

    const linkSource = `data:${mimeType};base64,${base64}`;
    const saveLink = document.createElement('a');

    saveLink.href = linkSource;
    saveLink.download = fileName;
    saveLink.click();
  }

  onUpdateNotesButtonClick(): void {
    const resultId = this.selectedSearchResult?.result_id;

    if (!this.notes.trim() || !resultId) {
      this.snackBar.open(
        'Please enter notes and ensure result ID is available.',
        'Close',
        {
          duration: 3000,
          panelClass: ['snackbar-error'],
        },
      );
      return;
    }

    const payload = {
      result_id: resultId,
      notes: this.notes.trim(),
    };

    this.advancedSearchService.updateNameNotes(payload).subscribe({
      next: () => {
        this.snackBar.open('Notes updated successfully!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
      },
      error: () => {
        this.snackBar.open('Failed to update notes.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      if (this.utilsService && this.utilsService.formatDateTime) {
        return this.utilsService.formatDateTime(dateString, 'MMM d, y');
      }
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  formatFieldLabel(key: unknown): string {
    // Convert to string if not already
    const keyStr = String(key || '');

    // Replace underscores with spaces
    let formatted = keyStr.replace(/_/g, ' ');

    // Replace camelCase with spaces (add space before capital letters)
    formatted = formatted.replace(/([a-z])([A-Z])/g, '$1 $2');

    // Capitalize first letter of each word
    return formatted
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  getFieldIcon(key: string): { icon: string; gradient: string } {
    const iconMap: { [key: string]: { icon: string; gradient: string } } = {
      email: {
        icon: 'email',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      address: {
        icon: 'location_on',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      },
      password: {
        icon: 'lock',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      },
      name: {
        icon: 'person',
        gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      },
      phone: {
        icon: 'phone',
        gradient: 'linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)',
      },
      mobile: {
        icon: 'smartphone',
        gradient: 'linear-gradient(135deg, #81fbb8 0%, #28c76f 100%)',
      },
      username: {
        icon: 'account_circle',
        gradient: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
      },
      user: {
        icon: 'person',
        gradient: 'linear-gradient(135deg, #c471f5 0%, #fa71cd 100%)',
      },
      date: {
        icon: 'calendar_today',
        gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
      },
      company: {
        icon: 'business',
        gradient: 'linear-gradient(135deg, #f83600 0%, #f9d423 100%)',
      },
      organization: {
        icon: 'business',
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      },
      website: {
        icon: 'language',
        gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      },
      url: {
        icon: 'link',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      },
      city: {
        icon: 'location_city',
        gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      },
      country: {
        icon: 'public',
        gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      },
      zip: {
        icon: 'markunread_mailbox',
        gradient: 'linear-gradient(135deg, #feb692 0%, #ea5455 100%)',
      },
      zipcode: {
        icon: 'markunread_mailbox',
        gradient: 'linear-gradient(135deg, #feb692 0%, #ea5455 100%)',
      },
      postal: {
        icon: 'markunread_mailbox',
        gradient: 'linear-gradient(135deg, #ce9ffc 0%, #7367f0 100%)',
      },
      state: {
        icon: 'map',
        gradient: 'linear-gradient(135deg, #90f7ec 0%, #32ccbc 100%)',
      },
      gender: {
        icon: 'wc',
        gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
      },
      age: {
        icon: 'cake',
        gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
      },
      dob: {
        icon: 'cake',
        gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      },
      birthdate: {
        icon: 'cake',
        gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      },
      job: {
        icon: 'work',
        gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
      },
      occupation: {
        icon: 'work',
        gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
      },
      nationality: {
        icon: 'public',
        gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      },
      passport: {
        icon: 'card_membership',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      passport_no: {
        icon: 'card_membership',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      id: {
        icon: 'fingerprint',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      id_number: {
        icon: 'fingerprint',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
    };

    // Normalize the key: lowercase and remove underscores/spaces
    const keyLower = key.toLowerCase().replace(/[_\s-]/g, '');

    // Check for exact match first
    if (iconMap[keyLower]) {
      return iconMap[keyLower];
    }

    // Check if the key contains any of the mapped keys
    for (const [mapKey, iconConfig] of Object.entries(iconMap)) {
      if (keyLower.includes(mapKey) || mapKey.includes(keyLower)) {
        return iconConfig;
      }
    }

    // Default icon if no match found
    return {
      icon: 'info',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    };
  }
}
